import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  Send,
  Loader2,
  CheckCircle,
} from 'lucide-react';
import { Card, Button, Input, Textarea } from '@azyntor-labs/nexus-ui';
import apiClient from '@/api/client';
import { toast } from '@/utils/toast';

interface Props {
  examId: string;
  examTitle: string;
}

export const EmailInvitationSender = ({ examId, examTitle }: Props) => {
  const queryClient = useQueryClient();
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState(`You're Invited: ${examTitle}`);
  const [message, setMessage] = useState('You have been invited to enroll in this exam. Click the link below to get started.');
  const [showResults, setShowResults] = useState(false);
  const [sendResults, setSendResults] = useState<any>(null);

  // Fetch invitation stats
  const { data: statsData } = useQuery({
    queryKey: ['email-invitation-stats', examId],
    queryFn: async () => {
      const response = await apiClient.get(`/exams/${examId}/invitations/stats`);
      return response.data;
    },
  });

  const stats = statsData?.data || {};

  // Fetch sent invitations
  const { data: invitationsData } = useQuery({
    queryKey: ['email-invitations', examId],
    queryFn: async () => {
      const response = await apiClient.get(`/exams/${examId}/invitations`);
      return response.data;
    },
  });

  const invitations = invitationsData?.data || [];

  // Send invitations mutation
  const sendMutation = useMutation({
    mutationFn: async () => {
      const recipientList = recipients
        .split(/[,\n]/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      if (recipientList.length === 0) {
        throw new Error('No valid recipients');
      }

      const response = await apiClient.post(`/exams/${examId}/send-invitations`, {
        recipients: recipientList,
        subject,
        message,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSendResults(data.data);
      setShowResults(true);
      toast.success(`Sent ${data.data.sent} invitation(s) successfully`);
      queryClient.invalidateQueries({ queryKey: ['email-invitations', examId] });
      queryClient.invalidateQueries({ queryKey: ['email-invitation-stats', examId] });
      setRecipients('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send invitations');
    },
  });

  const handleSend = () => {
    if (!recipients.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }
    sendMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Email Invitations</h3>
          <p className="text-sm text-gray-400">Send professional enrollment invitations</p>
        </div>
        {stats.total_sent > 0 && (
          <div className="flex gap-4 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-teal-400">{stats.total_sent || 0}</p>
              <p className="text-gray-500">Sent</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-400">{stats.opened || 0}</p>
              <p className="text-gray-500">Opened</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.enrolled || 0}</p>
              <p className="text-gray-500">Enrolled</p>
            </div>
          </div>
        )}
      </div>

      {/* Send Form */}
      <Card className="bg-[#12121a]/80 border-white/[0.06] p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Recipients <span className="text-gray-500">(comma or newline separated)</span>
            </label>
            <Textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="student1@example.com, student2@example.com&#10;student3@example.com"
              rows={4}
              className="font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {recipients.split(/[,\n]/).filter((e) => e.trim()).length} recipient(s)
            </p>
          </div>

          <Input
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />

          <Textarea
            label="Custom Message (Optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a personal message to the invitation"
            rows={3}
          />

          <Button
            onClick={handleSend}
            disabled={sendMutation.isPending || !recipients.trim()}
            className="w-full bg-teal-500 hover:bg-teal-600 text-white"
            leftIcon={
              sendMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )
            }
          >
            {sendMutation.isPending ? 'Sending...' : 'Send Invitations'}
          </Button>
        </div>
      </Card>

      {/* Send Results */}
      {showResults && sendResults && (
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-white mb-2">Invitations Sent</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-lg font-bold text-white">{sendResults.total}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Sent</p>
                  <p className="text-lg font-bold text-emerald-400">{sendResults.sent}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Failed</p>
                  <p className="text-lg font-bold text-red-400">{sendResults.failed}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Sent Invitations List */}
      {invitations.length > 0 && (
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-6">
          <h4 className="font-semibold text-white mb-4">Recent Invitations</h4>
          <div className="space-y-2">
            {invitations.slice(0, 10).map((inv: any) => (
              <div
                key={inv.id}
                className="flex items-center justify-between p-3 bg-[#0a0a0f] rounded-lg border border-white/[0.06]"
              >
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-white text-sm font-medium">{inv.recipient_email}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(inv.sent_at || inv.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {inv.opened_at && (
                    <span className="text-xs px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded">
                      Opened
                    </span>
                  )}
                  {inv.enrolled_at && (
                    <span className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded">
                      Enrolled
                    </span>
                  )}
                  <span className={`text-xs px-2 py-1 rounded ${
                    inv.status === 'sent' || inv.status === 'delivered'
                      ? 'bg-teal-500/10 text-teal-400'
                      : inv.status === 'failed' || inv.status === 'bounced'
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-gray-500/10 text-gray-400'
                  }`}>
                    {inv.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
